<template>
  <div>
    <SpinnerLoading v-if="isLoading" color="blue" style="margin-top: 58px;" />
    <div v-else class="schedule-editor">
      <div class="time-row">
        <label class="time-label">{{ $t('startTime') }}</label>
        <input
          v-model="localFrom"
          type="time"
          class="time-input"
          :class="{ 'error-input': timeErrors.from }"
        >
      </div>
      <div class="time-row">
        <label class="time-label">{{ $t('endTime') }}</label>
        <input
          v-model="localTo"
          type="time"
          class="time-input"
          :class="{ 'error-input': timeErrors.to }"
        >
      </div>
      <div v-if="scheduleErrors.length" class="error-msg">
        <div v-for="(error, index) in scheduleErrors" :key="index">
          {{ error }}
        </div>
      </div>
      <div class="days-selector">
        <button
          v-for="(day, index) in localDays"
          :key="index"
          class="day-button"
          :class="{ active: day.enabled }"
          @click="toggleDay(index)"
        >
          {{ day.label }}
        </button>
      </div>
      <div class="schedule-button">
        <CamsButton
          priority="primary"
          :disabled="!hasActiveDays"
          @click="saveSchedule"
        >
          {{ $t('save') }}
        </CamsButton>
        <CamsButton class="cancel" priority="secondary" @click="closeDialog">
          {{ $t('cancel') }}
        </CamsButton>
      </div>
    </div>
  </div>
</template>

<script>
import {ACTION_CREATE_SCHEDULE, ACTION_EDIT_SCHEDULE, DAYS_OF_WEEK} from "@/store/analytics/schedules/index.js";
import {createEntityMixin} from "@/components/pacs/mixins.js";
/**
 *  Компонент создания и редактирования расписания
 *
 **/
export default {
  name: 'ScheduleAnalytics',
  mixins: [createEntityMixin],
  props: {
    analyticType: {
      type: String,
      default: '',
    },
    cameraNumber: {
      type: String,
      default: '',
    },
    from: {
      type: String,
      default: '09:00',
    },
    to: {
      type: String,
      default: '18:00',
    },
    days: {
      type: Array,
      default: () => [],
    },
    scheduleId: {
      type: Number,
      default: null,
    },
    settings: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      isLoading: false,
      localFrom: this.from,
      localTo: this.to,
      localDays: DAYS_OF_WEEK.map(day => ({
        ...day,
        enabled: this.days.includes(day.label)
      })),
      timeErrors: {
        from: '',
        to: '',
      },
      scheduleErrors: [],
    };
  },
  methods: {
    /**
     *  Метод создания и редактирования расписания два в одном. Если пришли с id, - редактируем.
     *
     **/
    async saveSchedule() {
      this.isLoading = true;
      const selectedLabels = this.localDays
        .filter(day => day.enabled)
        .map(day => day.label);
      this.clearScheduleErrors();
      try {
        if (this.scheduleId) {
          await this.$store.dispatch(`analytics/schedules/${ACTION_EDIT_SCHEDULE}`, {
            cameraNumber: this.cameraNumber,
            scheduleType: this.analyticType,
            scheduleId: this.scheduleId,
            schedule: {
              from: this.localFrom,
              to: this.localTo,
              days: selectedLabels,
            },
          });
          this.closeDialog(true);
        } else {
          await this.$store.dispatch(`analytics/schedules/${ACTION_CREATE_SCHEDULE}`, {
            cameraNumber: this.cameraNumber,
            scheduleType: this.analyticType,
            schedule: {
              from: this.localFrom,
              to: this.localTo,
              days: selectedLabels,
            },
          });
          this.closeDialog(true);
        }
      } catch (e) {
        console.error("Ошибка при сохранении расписания", e);
        this.applyScheduleErrors(e);
      } finally {
        this.isLoading = false;
      }
    },

    clearScheduleErrors() {
      this.timeErrors.from = '';
      this.timeErrors.to = '';
      this.scheduleErrors = [];
    },

    applyScheduleErrors(error) {
      const parsedErrors = this.parseScheduleErrors(error);
      this.timeErrors.from = parsedErrors.from;
      this.timeErrors.to = parsedErrors.to;
      this.scheduleErrors = parsedErrors.messages.length
        ? parsedErrors.messages
        : [this.$t('saveScheduleError') || 'Ошибка при сохранении расписания'];
    },

    parseScheduleErrors(error) {
      const fields = error?.fields || error?.response?.data?.fields || {},
            result = {
              from: '',
              to: '',
              messages: [],
            };

      this.addTimeFieldErrors(result, fields.start_time, 'from');
      this.addTimeFieldErrors(result, fields.end_time, 'to');
      this.addSettingsErrors(result, fields.settings);

      Object.keys(fields)
        .filter(fieldName => !['start_time', 'end_time', 'settings'].includes(fieldName))
        .forEach(fieldName => {
          this.addScheduleErrorMessages(result, fields[fieldName]);
        });

      const apiError = error?.error || error?.response?.data?.error;
      if (!result.messages.length && apiError) {
        result.messages.push(apiError);
      }

      result.messages = [...new Set(result.messages)];
      return result;
    },

    addSettingsErrors(result, settingsErrors) {
      if (!settingsErrors) {
        return;
      }
      if (!Array.isArray(settingsErrors)) {
        this.addScheduleErrorMessages(result, settingsErrors);
        return;
      }

      settingsErrors.forEach((settingErrors) => {
        if (!settingErrors || typeof settingErrors !== 'object' || Array.isArray(settingErrors)) {
          this.addScheduleErrorMessages(result, settingErrors);
          return;
        }

        this.addTimeFieldErrors(result, settingErrors.start_time, 'from');
        this.addTimeFieldErrors(result, settingErrors.end_time, 'to');
        Object.keys(settingErrors)
          .filter(fieldName => !['start_time', 'end_time'].includes(fieldName))
          .forEach(fieldName => {
            this.addScheduleErrorMessages(result, settingErrors[fieldName]);
          });
      });
    },

    addTimeFieldErrors(result, errors, timeField) {
      const messages = this.collectScheduleErrorMessages(errors);
      if (!messages.length) {
        return;
      }

      result[timeField] = messages[0];
      if (timeField === 'from' && messages.some(message => message.includes('end_time'))) {
        result.to = result.to || messages[0];
      }
      if (timeField === 'to' && messages.some(message => message.includes('start_time'))) {
        result.from = result.from || messages[0];
      }
      result.messages.push(...messages);
    },

    addScheduleErrorMessages(result, errors) {
      result.messages.push(...this.collectScheduleErrorMessages(errors));
    },

    collectScheduleErrorMessages(errors) {
      if (!errors) {
        return [];
      }
      if (typeof errors === 'string') {
        return [errors];
      }
      if (Array.isArray(errors)) {
        return errors.reduce((messages, item) => {
          return messages.concat(this.collectScheduleErrorMessages(item));
        }, []);
      }
      if (typeof errors === 'object') {
        return Object.values(errors).reduce((messages, item) => {
          return messages.concat(this.collectScheduleErrorMessages(item));
        }, []);
      }
      return [String(errors)];
    },

    toggleDay(index) {
      this.localDays[index].enabled = !this.localDays[index].enabled;
    },
  },
  computed: {
    /**
     *  Если не выбран день недели сохранение невозможно.
     *
     **/
    hasActiveDays() {
      return this.localDays.some(day => day.enabled);
    }
  },
};
</script>

<style lang="scss">
@use "./style.scss";
</style>
